import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AdminSidebar from '../../components/admin/AdminSidebar';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Textarea } from '../../components/ui/textarea';
import { ArrowLeft, User, Mail, Phone, Calendar, DollarSign, MapPin, Clock, Plane, Package } from 'lucide-react';

const API_BASE = window.location.origin || '';

const AdminCustomerDetailPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [customer, setCustomer] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newNote, setNewNote] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('vipgez_admin_token');
    if (!token) {
      navigate('/login-goturkeytransfer');
      return;
    }
    fetchCustomerDetail();
  }, [navigate, id]);

  const fetchCustomerDetail = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('vipgez_admin_token');
      const response = await fetch(`${API_BASE}/api/admin/customers/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();

      if (data.success) {
        setCustomer(data.customer);
        setBookings(data.bookings || []);
      }
    } catch (error) {
      console.error('Failed to fetch customer:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'confirmed': 'bg-green-500/10 text-green-500',
      'confirmed_cash': 'bg-green-500/10 text-green-500',
      'pending': 'bg-yellow-500/10 text-yellow-500',
      'cancelled': 'bg-red-500/10 text-red-500',
      'completed': 'bg-blue-500/10 text-blue-500',
    };
    return colors[status] || 'bg-gray-500/10 text-gray-500';
  };

  if (loading) {
    return (
      <div className="flex min-h-screen bg-background admin-theme" data-admin-panel="true">
        <AdminSidebar />
        <main className="flex-1 p-6 lg:p-8 flex items-center justify-center">
          <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full"></div>
        </main>
      </div>
    );
  }

  if (!customer) {
    return (
      <div className="flex min-h-screen bg-background admin-theme" data-admin-panel="true">
        <AdminSidebar />
        <main className="flex-1 p-6 lg:p-8">
          <div className="text-center py-12">
            <p className="text-muted-foreground">Customer not found</p>
            <Button onClick={() => navigate('/admin/customers')} className="mt-4">
              Back to Customers
            </Button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-background admin-theme" data-admin-panel="true">
      <AdminSidebar />
      
      <main className="flex-1 p-6 lg:p-8 overflow-auto">
        <div className="max-w-6xl mx-auto">
          {/* Back Button */}
          <Button
            variant="outline"
            onClick={() => navigate('/admin/customers')}
            className="mb-6 text-foreground hover:text-foreground"
            data-testid="back-to-customers"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Customers
          </Button>

          {/* Customer Header */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
            {/* Main Info */}
            <Card className="bg-secondary border-border lg:col-span-2">
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
                    <User className="h-8 w-8 text-primary" />
                  </div>
                  <div className="flex-1">
                    <h1 className="text-2xl font-bold text-foreground" data-testid="customer-name">
                      {customer.name || 'Unknown'}
                    </h1>
                    <div className="mt-2 space-y-2">
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Mail className="h-4 w-4" />
                        <span>{customer.email}</span>
                      </div>
                      {customer.phone && (
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <Phone className="h-4 w-4" />
                          <span>{customer.phone}</span>
                        </div>
                      )}
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Calendar className="h-4 w-4" />
                        <span>Joined {customer.created_at ? new Date(customer.created_at).toLocaleDateString() : 'N/A'}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Stats */}
            <Card className="bg-secondary border-border">
              <CardContent className="pt-6">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Total Bookings</span>
                    <span className="text-xl font-bold text-foreground">{customer.total_bookings || 0}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Total Spent</span>
                    <span className="text-xl font-bold text-green-500">€{(customer.total_spent || 0).toFixed(2)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Language</span>
                    <span className="text-foreground">{customer.locale === 'tr' ? 'Turkish' : 'English'}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Bookings History */}
          <Card className="bg-secondary border-border">
            <CardHeader>
              <CardTitle className="text-foreground">Booking History</CardTitle>
            </CardHeader>
            <CardContent>
              {bookings.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No bookings yet
                </div>
              ) : (
                <div className="space-y-4">
                  {bookings.map((booking, idx) => (
                    <div
                      key={booking.booking_ref || idx}
                      className="p-4 border border-border rounded-lg hover:bg-muted/30 transition-colors cursor-pointer"
                      onClick={() => navigate(`/admin/bookings/${booking.booking_ref}`)}
                      data-testid={`booking-${idx}`}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <p className="font-mono font-medium text-primary">{booking.booking_ref}</p>
                          <p className="text-sm text-muted-foreground">
                            {booking.trip_date} at {booking.trip_time}
                          </p>
                        </div>
                        <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(booking.status)}`}>
                          {booking.status}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div className="flex items-start gap-2">
                          <MapPin className="h-4 w-4 text-muted-foreground mt-0.5" />
                          <div>
                            <p className="text-muted-foreground">From</p>
                            <p className="text-foreground">{booking.pickup_location}</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-2">
                          <MapPin className="h-4 w-4 text-primary mt-0.5" />
                          <div>
                            <p className="text-muted-foreground">To</p>
                            <p className="text-foreground">{booking.dropoff_location}</p>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center justify-between mt-3 pt-3 border-t border-border/50">
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          <span>{booking.passengers} passengers</span>
                          <span>{booking.vehicle_name || 'Standard'}</span>
                        </div>
                        <span className="font-medium text-foreground">€{(booking.total || 0).toFixed(2)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default AdminCustomerDetailPage;
